import { useState } from 'react';
import { IconSend } from '@tabler/icons-react';
import { SendModalShell } from '../send/SendModalShell.js';
import { Button } from '../ui/Button.js';
import { Input } from '../ui/Input.js';
import { Label } from '../ui/Label.js';
import { Select } from '../ui/Select.js';
import { ApiError } from '../../lib/api.js';
import { useCreateInvitation } from '../../hooks/useTeam.js';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called with the recipient email after a successful send. */
  onSuccess: (email: string) => void;
}

type InviteRole = 'consultant' | 'admin';

export function InviteModal({ open, onClose, onSuccess }: Props) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<InviteRole>('consultant');
  const [error, setError] = useState<string | null>(null);
  const create = useCreateInvitation();

  function reset() {
    setEmail('');
    setRole('consultant');
    setError(null);
  }

  function handleClose() {
    if (create.isPending) return;
    reset();
    onClose();
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const sentTo = email.trim();
      await create.mutateAsync({ email: sentTo, role });
      reset();
      onSuccess(sentTo);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
    }
  }

  return (
    <SendModalShell open={open} onClose={handleClose} blocking={create.isPending}>
      <form onSubmit={handleSubmit}>
        <div className="px-7 pt-6 pb-1">
          <h2 className="font-serif text-2xl text-ink tracking-tight">Invite a teammate</h2>
          <p className="text-sm text-muted mt-1">
            They&rsquo;ll get an email with a link to set up their account.
          </p>
        </div>

        <div className="px-7 py-4 space-y-4">
          <div>
            <Label htmlFor="invite-email">Email</Label>
            <Input
              id="invite-email"
              type="email"
              autoComplete="off"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@agency.com.au"
            />
          </div>

          <div>
            <Label htmlFor="invite-role">Role</Label>
            <Select
              id="invite-role"
              value={role}
              onChange={(e) => setRole(e.target.value as InviteRole)}
            >
              <option value="consultant">Consultant</option>
              <option value="admin">Agency admin</option>
            </Select>
            <p className="text-xs text-muted mt-1.5">
              {role === 'consultant'
                ? 'Can browse the catalogue and send course info to students.'
                : 'Full access, including managing the team and private courses.'}
            </p>
          </div>

          {error && (
            <div
              className="rounded-md px-3 py-2 text-[13px]"
              style={{ backgroundColor: '#FBEAEA', color: '#A83232', border: '1px solid #F0D0D0' }}
            >
              {error}
            </div>
          )}
        </div>

        <div
          className="px-7 py-5 flex items-center justify-end gap-3 border-t"
          style={{ borderColor: '#E8E2D5' }}
        >
          <Button type="button" variant="ghost" onClick={handleClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button type="submit" loading={create.isPending} leftIcon={<IconSend size={16} />}>
            Send invite
          </Button>
        </div>
      </form>
    </SendModalShell>
  );
}
